import { useCallback, useRef } from "react";

export interface TabDef<K extends string = string> {
  key: K;
  label: string;
  /** Optional count rendered as a muted pill after the label. */
  badge?: number | null;
}

/** Pill tab bar. Visual matches the day selector on the user-activity page
 *  (`UserActivity.tsx`) so the two `/audit` routes read as one surface.
 *
 *  Deliberately dumb — it owns no state and no URL. Pages sync `value` to a
 *  search param themselves, which keeps a tab bookmarkable without this
 *  component reaching for the router. */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
  className = "",
  "aria-label": ariaLabel,
}: {
  tabs: readonly TabDef<K>[];
  value: K;
  onChange: (key: K) => void;
  className?: string;
  "aria-label"?: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Arrow keys move between tabs, as the tablist pattern expects — the pills
  // are buttons, so without this a keyboard user has to Tab through each one.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!delta) return;
      e.preventDefault();
      const i = tabs.findIndex((t) => t.key === value);
      const next = tabs[(i + delta + tabs.length) % tabs.length];
      onChange(next.key);
      refs.current[next.key]?.focus();
    },
    [tabs, value, onChange],
  );

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={`flex gap-1.5 overflow-x-auto scrollbar-thin pb-1 ${className}`}
    >
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            ref={(el) => {
              refs.current[t.key] = el;
            }}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-[13px] font-medium transition ${
              active
                ? "bg-accent-500/15 text-accent-200 border border-accent-500/30"
                : "text-slate-400 hover:text-slate-200 border border-white/5"
            }`}
          >
            {t.label}
            {t.badge != null && (
              <span
                className={`ml-1.5 tabular-nums text-[11px] ${
                  active ? "text-accent-300/80" : "text-slate-600"
                }`}
              >
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
